/**
 * Stripe Checkout return handler — ACCOUNT (Trainnova Coach) billing.
 *
 * /billing/success — polls the backend ACCOUNT status (webhook-authoritative).
 *   no access yet → "Activating..." (retryable)
 *   has_access    → team exists → normal app ; no team → Welcome/Create Team
 * /billing/cancel  — account not activated; offer to resume the SAME account checkout.
 *
 * The frontend NEVER assumes success from the redirect and never queries Stripe
 * directly — backend/account entitlement is authoritative.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { teamBillingAction } from '../lib/teamBillingAction';
import { useAuth } from '../context/AuthContext';
import { getTeamBillingStatus, createTeamCheckout, getAccountBillingStatus, createAccountCheckout } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, RefreshCw } from 'lucide-react';

const MAX_POLLS = 8;

function AccountBillingReturn() {
  const { t } = useTranslation(['self_onboarding', 'common']);
  const { isAuthenticated, loading: authLoading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isCancel = location.pathname.includes('/cancel');

  const [phase, setPhase] = useState(isCancel ? 'cancel' : 'checking'); // checking | activating | cancel
  const [busy, setBusy] = useState(false);

  const goAfterActivation = useCallback(async () => {
    // has_access confirmed by backend → route based on whether a team exists.
    let hasTeam = false;
    try {
      const u = await refreshUser();
      hasTeam = (u?.team_ids?.length || 0) > 0 || !!u?.active_team_id || !!u?.team_id;
    } catch { /* noop */ }
    if (hasTeam) navigate('/', { replace: true });
    else navigate('/onboarding?activated=1', { replace: true });
  }, [refreshUser, navigate]);

  const runPoll = useCallback(async () => {
    setPhase('checking');
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const res = await getAccountBillingStatus();
        if (res.data?.has_access) { await goAfterActivation(); return; }
      } catch { /* transient — keep polling */ }
      if (tries >= MAX_POLLS) { setPhase('activating'); return; }
      setTimeout(tick, 2000);
    };
    tick();
  }, [goAfterActivation]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login', { replace: true }); return; }
    if (isCancel) { setPhase('cancel'); return; }
    runPoll();
  }, [authLoading, isAuthenticated, isCancel, navigate, runPoll]);

  const resumeCheckout = async () => {
    setBusy(true);
    try {
      const res = await createAccountCheckout(window.location.origin);
      window.location.href = res.data.checkout_url;
    } catch {
      setBusy(false);
      navigate('/onboarding', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        {(phase === 'checking' || phase === 'activating') && (
          <Card className="bg-card border-border text-center" data-testid="billing-activating">
            <CardContent className="py-10 space-y-4">
              {phase === 'checking'
                ? <Loader2 className="h-10 w-10 text-primary animate-spin mx-auto" />
                : <RefreshCw className="h-10 w-10 text-primary mx-auto" />}
              <div>
                <p className="text-lg font-semibold">{t('self_onboarding:activating.title')}</p>
                <p className="text-sm text-muted-foreground mt-1">{t('self_onboarding:activating.body')}</p>
              </div>
              {phase === 'activating' && (
                <Button variant="outline" onClick={runPoll} data-testid="activating-retry-btn">
                  <RefreshCw className="mr-2 h-4 w-4" /> {t('self_onboarding:activating.retry')}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {phase === 'cancel' && (
          <Card className="bg-card border-border" data-testid="billing-cancel">
            <CardHeader className="text-center">
              <CardTitle>{t('self_onboarding:cancel.title')}</CardTitle>
              <CardDescription>{t('self_onboarding:cancel.body')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button onClick={resumeCheckout} disabled={busy} className="w-full h-12" data-testid="cancel-continue-btn">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {busy ? t('self_onboarding:cancel.starting') : t('self_onboarding:cancel.continue')}
              </Button>
              <Button variant="ghost" onClick={() => navigate('/onboarding', { replace: true })} disabled={busy}
                className="w-full h-12" data-testid="cancel-back-btn">
                {t('self_onboarding:common.back')}
              </Button>
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
}


export default function BillingReturn() {
  const location = useLocation();
  const { user, sessionVersion } = useAuth();
  const teamId = new URLSearchParams(location.search).get('team_id');
  return teamId ? <TeamBillingReturn key={JSON.stringify([teamId, user?.id, sessionVersion, location.pathname])} teamId={teamId} /> : <AccountBillingReturn />;
}

function TeamBillingReturn({ teamId }) {
  const { user, loading: authLoading, refreshTeams, changeActiveTeam } = useAuth();
  const billingAlive = useRef(true);
  const resumeLock = useRef(false);
  useEffect(()=>{billingAlive.current=true;return()=>{billingAlive.current=false;};},[]);
  const { i18n } = useTranslation();
  const sv = (i18n.language || 'sv').startsWith('sv');
  const location = useLocation();
  const navigate = useNavigate();
  const cancelled = location.pathname.includes('/cancel');
  const [state, setState] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (authLoading) return undefined;
    if (!user) { navigate('/login', { replace: true }); return undefined; }
    let active = true, timer, tries = 0;
    const poll = async () => {
      try {
        const response = await getTeamBillingStatus(teamId);
        if (!active) return;
        if (response.data.team_id !== teamId) throw new Error('context');
        setState(response.data); setError(null);
        if (response.data.has_access) { refreshTeams?.().catch(() => {}); return; }
      } catch { if (active) setError(sv ? 'Status kunde inte hämtas. Försök igen.' : 'Could not load status. Please retry.'); }
      if (active && !cancelled && ++tries < MAX_POLLS) timer = setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; clearTimeout(timer); };
  }, [teamId, user?.id, authLoading, cancelled, attempt, navigate]);
  const back = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      if (state?.creation_flow !== 'additional_team_v1') {
        navigate(state?.organization_id ? (user?.global_role === 'app_admin' ? `/platform-admin/orgs/${state.organization_id}` : `/org/${state.organization_id}/teams`) : '/');
        return;
      }
      if (state?.has_access) { await refreshTeams?.(); await changeActiveTeam(teamId); navigate('/oversikt'); }
      else navigate('/create-team');
    } catch { setError(sv ? 'Kunde inte öppna laget. Försök igen.' : 'Could not open the team. Please retry.'); }
    finally { setBusy(false); }
  };
  const resume = async () => {
    if (busy || resumeLock.current) return;
    resumeLock.current=true;
    setBusy(true); setError(null);
    try {
      if (state?.creation_flow === 'additional_team_v1') {
        const action = await teamBillingAction(teamId,()=>billingAlive.current);
        if (action?.kind === 'active') { setBusy(false); setAttempt(v=>v+1); }
        else if (action) window.location.assign(action.url);
        return;
      }
      const response = await createTeamCheckout(teamId, window.location.origin);
      const url = new URL(response.data.checkout_url);
      if (url.protocol !== 'https:' || url.hostname !== 'checkout.stripe.com') throw new Error('url');
      if(billingAlive.current)window.location.assign(url.href);
    } catch (e) {
      if(billingAlive.current)setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : (sv ? 'Aktiveringen kunde inte öppnas.' : 'Could not open activation.'));
    } finally {
      resumeLock.current=false;
      if(billingAlive.current)setBusy(false);
    }
  };
  return <div className="flex min-h-screen items-center justify-center bg-background p-4"><Card className="w-full max-w-md border-border bg-card"><CardHeader>
    <CardTitle>{state?.has_access ? (sv ? 'Lagets abonnemang är aktiverat' : 'Team subscription activated') : cancelled ? (sv ? 'Aktiveringen är inte slutförd' : 'Activation is incomplete') : (sv ? 'Kontrollerar lagets abonnemang' : 'Checking the team subscription')}</CardTitle>
    <CardDescription>{state?.team_name || (sv ? 'Laget är sparat. Du behöver inte skapa det igen.' : 'The team is saved. There is no need to create it again.')}</CardDescription>
  </CardHeader><CardContent className="space-y-3">
    {!state?.has_access && <p className="text-sm text-muted-foreground">{sv ? 'Åtkomst ges först när betalningssystemet har bekräftat abonnemanget. Du kan uppdatera status eller återvända till laglistan.' : 'Access starts after the payment system confirms the subscription. Refresh the status or return to the team list.'}</p>}
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {!state?.has_access && <Button variant="outline" className="w-full" onClick={() => setAttempt(v => v + 1)}><RefreshCw className="mr-2 h-4 w-4" />{sv ? 'Uppdatera status' : 'Refresh status'}</Button>}
    {cancelled && !state?.has_access && <Button disabled={busy} className="w-full" onClick={resume}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{sv ? 'Fortsätt aktiveringen' : 'Continue activation'}</Button>}
    <Button variant={state?.has_access ? 'default' : 'ghost'} className="w-full" disabled={busy || !state} onClick={back}>{state?.creation_flow !== 'additional_team_v1' ? (sv ? 'Till laglistan' : 'Back to teams') : state?.has_access ? (sv ? 'Öppna laget' : 'Open team') : (sv ? 'Till mina nya lag' : 'My new teams')}</Button>
  </CardContent></Card></div>;
}
